import { useCallback, useEffect, useRef, useState } from "react";
import { scoreField, type ScoreDraftIdentity, type ScorableField } from "./qualityApi";

export type FieldQuality =
    | { status: "idle" }
    | { status: "scoring" }
    | { status: "scored"; score: number; feedback: string }
    | { status: "error"; message: string };

const IDLE: FieldQuality = { status: "idle" };

/** Scores Discover fields when the user moves off one.
 *
 * The score belongs to the exact text it was computed from: `evaluate` skips
 * text it has already scored (so tabbing back through the form costs nothing),
 * and `invalidate` — called as the user types — drops the score and aborts any
 * in-flight request so a stale verdict never sits under edited text. */
export function useFieldQuality(
    token: string | null | undefined,
    options: { enabled?: boolean; ensurePersisted?: () => Promise<ScoreDraftIdentity> } = {}
) {
    const enabled = options.enabled ?? true;
    const [quality, setQuality] = useState<Record<ScorableField, FieldQuality>>({
        problem: IDLE,
        solution: IDLE,
        personas: IDLE
    });
    const requests = useRef<Partial<Record<ScorableField, AbortController>>>({});
    const scoredText = useRef<Partial<Record<ScorableField, string>>>({});

    const abort = useCallback((field: ScorableField) => {
        requests.current[field]?.abort();
        delete requests.current[field];
    }, []);

    useEffect(() => {
        const inFlight = requests.current;
        return () => Object.values(inFlight).forEach(controller => controller?.abort());
    }, []);

    const invalidate = useCallback(
        (field: ScorableField) => {
            abort(field);
            delete scoredText.current[field];
            setQuality(current => (current[field].status === "idle" ? current : { ...current, [field]: IDLE }));
        },
        [abort]
    );

    const evaluate = useCallback(
        (field: ScorableField, value: string) => {
            const answer = value.trim();
            if (!enabled || !token || answer === "" || scoredText.current[field] === answer) return;
            abort(field);
            const controller = new AbortController();
            requests.current[field] = controller;
            setQuality(current => ({ ...current, [field]: { status: "scoring" } }));
            void (async () => {
                try {
                    const draft = await options.ensurePersisted?.();
                    if (controller.signal.aborted) return;
                    const result = await scoreField(field, answer, token, { signal: controller.signal, draft });
                    if (controller.signal.aborted) return;
                    scoredText.current[field] = answer;
                    setQuality(current => ({
                        ...current,
                        [field]: { status: "scored", score: result.score, feedback: result.feedback }
                    }));
                } catch (error) {
                    if (controller.signal.aborted) return;
                    setQuality(current => ({
                        ...current,
                        [field]: {
                            status: "error",
                            message: error instanceof Error ? error.message : "Evaluation failed — try again."
                        }
                    }));
                } finally {
                    if (requests.current[field] === controller) delete requests.current[field];
                }
            })();
        },
        [abort, enabled, options.ensurePersisted, token]
    );

    return { quality, evaluate, invalidate };
}
